/**
 * HostOrderPlanCard — cartão de uma host_order proposta no chat (Task 11,
 * TASK-10361).
 *
 * **Não é só o cartão do degrau 3, apesar do nome** — e confundir isso foi um
 * defeito real. `ActionProposalCard` despacha TODO `action_type: 'host_order'`
 * para cá, degrau 2 incluído (`docker_restart`, `service_restart`,
 * `docker_recreate`), e degrau 2 não tem plano: mutação reversível não passa
 * por dry-run. Até a rodada de conserto 2 da Task 16 o card exigia `plan_id`
 * para renderizar os botões, então reiniciar um container pelo chat — o caso
 * de uso mais comum do sistema — mostrava "Este plano não está mais
 * disponível para confirmação" e nenhum botão. Quem decide se há plano a
 * esperar é `plano.degrau`, vindo do catálogo (D5); ver `exigePlano`.
 *
 * Mostra o que uma ordem irreversível (limpar logs, podar Docker, zerar
 * arquivo) vai fazer ANTES do clique final, e o botão que confirma. Três
 * decisões que vieram do brief da task, não do desenho livre:
 *
 * 1. O gate do botão é `canConfirm`, calculado no SERVIDOR
 *    (`chat_bot._pode_confirmar_host_order` — permissão fina + piso +
 *    ambiente). Este card NUNCA decide sozinho se pode confirmar; ele só
 *    OBEDECE o que o backend mandou. `canConfirm=false` esconde o botão —
 *    não o desabilita — porque um botão desabilitado sem explicação é pior
 *    que a ausência dele (a mesma lição que motivou existir `can_confirm`
 *    no lugar do mapa estático do frontend).
 * 2. `plan_id: null` é uma forma de retorno válida ("nada a limpar" — um
 *    dry-run que não achou nada), não um erro. Este card nunca assume que
 *    `plan_id`/`itens` estão presentes.
 * 3. Plano ESTIMADO (`tipo_plano: 'estimado'`) nasce sem estimativa (spec
 *    §7.2 — os comandos que mediriam não existem como primitiva ainda).
 *    Por isso este card NUNCA mostra um número de bytes para plano
 *    estimado, mesmo que um venha preenchido: exibir o `reclaimable` do
 *    daemon como promessa seria pior que não mostrar nada. Só a tabela
 *    (que também não existe para plano estimado — não há itens) fica de
 *    fora; o total do plano ENUMERADO usa `estimativa_bytes` normalmente.
 */
import { useState } from 'react'
import { Alert, Badge, Button, Card, Code, Group, Stack, Table, Text, TextInput } from '@mantine/core'
import { IconAlertTriangle, IconServerBolt, IconTrash } from '@tabler/icons-react'

import { useEstadoDoPlano } from '../../../hooks/useChat'
import { formatarBytes } from './bytes'
import { FONTE_DADO } from './Dado'
import { CriarRegraDaOrdem } from './CriarRegraDaOrdem'

// ── Tipos ──────────────────────────────────────────────────────────────────

export interface HostOrderPlanItem {
  inode: number
  bytes: number
  path: string
}

export interface HostOrderPlano {
  verbo: string
  plan_id: string | null
  itens: HostOrderPlanItem[] | null
  estimativa_bytes: number | null
  tipo_plano: 'enumerado' | 'estimado'
  /** `nada_a_limpar` | `plano_pendente` — quando o backend a envia. */
  status?: string
  /** ISO-8601 de `host_order_plans.expires_at` (Task 16, TASK-10361). */
  expires_at?: string | null
  /** Degrau do verbo, do CATÁLOGO (D5) — 1 leitura, 2 mutação reversível,
   * 3 irreversível. Só o degrau 3 tem plano. `undefined` = proposta gravada
   * antes desta task; ver `exigePlano`. */
  degrau?: number
  /** Em produção a ordem de degrau 3 exige a aprovação de OUTRO integrante do
   * time DevOps (spec 2026-09-27 §12). Quem pode aprovar decide o servidor. */
  segunda_aprovacao_exigida?: boolean
  /** Teto MEDIDO da alternativa escolhida (bytes), quando a proposta veio do
   * cartão de alternativas. É "até", nunca estimativa do plano (§5). */
  teto_bytes?: number | null
  /** O comando que a primitiva monta — o mesmo que o plano executa. */
  comando?: string | null
}

export type HostOrderCardStatus = 'pending' | 'executed' | 'failed' | 'declined'

/** Uso de uma montagem medido pelo `df` (MR 3, TASK-11871). */
export interface HostOrderUso {
  uso_pct: number
  tamanho_bytes?: number | null
}

/** A remedição da montagem do alerta depois da ordem. `depois` nulo = não mediu,
 * e `motivo` diz por quê — o cartão nunca mostra percentual estimado. */
export interface HostOrderRemedicao {
  montagem: string
  limiar_pct: number | null
  antes: HostOrderUso | null
  depois: HostOrderUso | null
  motivo: string | null
  /** Host do ALERTA de onde a ordem nasceu (`remedicao_alvo.alerta_host`), não o Name
   * tag da máquina. Ausente em ordem antiga — aí não há regra a criar dela. */
  alerta_host?: string | null
}

/** O resumo do resultado que o confirm persiste em `proposed_action.result`. */
export interface HostOrderResultado {
  status?: string | null
  bytes_liberados?: number | null
  gmud_id?: number | null
  remedicao?: HostOrderRemedicao | null
}

interface Props {
  plano: HostOrderPlano
  /** Calculado pelo SERVIDOR (`ProposedAction.can_confirm`) — nunca por mapa local. */
  canConfirm: boolean
  /**
   * TASK-10810 (spec §5, I3 da rodada 1 de revisão): motivo de
   * `canConfirm=false` quando é o portão de PRD (produção sem laudo do
   * @DevOps para o recurso NESTE turno) — `host_order` também está em
   * `chat_bot.ACOES_MUTATIVAS`, então pode ser vetado por ele, não só pelas
   * checagens de `_pode_confirmar_host_order`. Sem isto, o usuário perdia o
   * botão em silêncio — a mesma lição do item 1 acima, aplicada ao motivo.
   */
  motivoBloqueio?: string | null
  /** Hostname/instância-alvo, mostrado como referência e (em produção) digitado de volta. */
  hostname: string
  /** Ambiente do tenant da conversa. Em produção, confirmar exige digitar o hostname. */
  isProd?: boolean
  status?: HostOrderCardStatus
  onConfirm?: () => void
  isConfirming?: boolean
  onDecline?: () => void
  isDeclining?: boolean
  /** Resultado persistido do confirm; com `remedicao`, o cartão executado mostra o
   * antes e o depois da montagem do alerta. */
  resultado?: HostOrderResultado | null
  /** Tenant da ordem (`params.tenant_id`). Sem ele, o cartão não oferece criar regra. */
  tenantId?: number | null
}

// ── Helpers ────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<HostOrderCardStatus, string> = {
  pending: 'Pendente',
  executed: 'Executado',
  failed: 'Falhou',
  declined: 'Recusado',
}

const STATUS_COLORS: Record<HostOrderCardStatus, string> = {
  pending: 'yellow',
  executed: 'teal',
  failed: 'red',
  declined: 'gray',
}

/** Caminho "normal" — mesma allowlist de `host_orders/service.py:_PATH_NORMAL_RE`.
 * Fora dela é sinal de nome hostil no disco; a exibição escapa, mas a
 * exclusão de verdade sempre foi por INODE (D9) — isto é só apresentação. */
const PATH_NORMAL_RE = /^[A-Za-z0-9._/-]+$/

function pathAnomalo(path: string): boolean {
  return !PATH_NORMAL_RE.test(path)
}

/** `expires_at` (ISO-8601) → `HH:MM` local, ou `null` quando não dá para ler.
 *
 * O plano vale 10 min contados do DRY-RUN — ou seja, do momento em que o
 * agente propôs, não do clique. Sem mostrar a validade, quem lê a conversa 11
 * minutos depois clica e recebe `plano_expirado` sem ter como saber que havia
 * prazo: o motivo estaria certo e a tela nunca teria dito nada. Só a HORA, não
 * a data — um plano de 10 minutos nunca cruza um dia de forma relevante para
 * quem está olhando o cartão, e a data faria a linha competir com o conteúdo.
 *
 * `null` (data ausente, string inválida) esconde a linha em vez de exibir
 * "Invalid Date": um horário que não se sabe é melhor não afirmar. */
function formatarValidade(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

const VERBO_LABELS: Record<string, string> = {
  // Degrau 2 — mutação reversível, sem plano. Passaram a aparecer aqui na
  // rodada de conserto 2: antes o card nunca mostrava botão para eles, então
  // o rótulo de fallback (`verbo.replace(/_/g, ' ')`) nunca incomodou.
  docker_restart: 'Reiniciar container',
  service_restart: 'Reiniciar serviço',
  docker_recreate: 'Recriar container (down + up)',
  limpar_logs_rotacionados: 'Limpar logs rotacionados',
  limpar_journal: 'Limpar journal (journalctl vacuum)',
  podar_docker: 'Podar imagens/volumes Docker não usados',
  truncate_zero: 'Zerar arquivo (truncar)',
}

function verboLabel(verbo: string): string {
  return VERBO_LABELS[verbo] ?? verbo.replace(/_/g, ' ')
}

// ── Componente ─────────────────────────────────────────────────────────────

export function HostOrderPlanCard({
  plano,
  canConfirm,
  motivoBloqueio,
  hostname,
  isProd = false,
  status = 'pending',
  onConfirm,
  isConfirming = false,
  onDecline,
  isDeclining = false,
  resultado = null,
  tenantId = null,
}: Props) {
  const [hostnameDigitado, setHostnameDigitado] = useState('')

  const isPending = status === 'pending'
  const semPlano = plano.plan_id == null
  const segunda = plano.segunda_aprovacao_exigida === true

  // **Regressão fechada na rodada de conserto 2.** Até aqui o card tratava
  // `semPlano` como "o plano sumiu", e `podeClicarConfirmar` exigia
  // `!semPlano` — mas degrau 2 (`docker_restart`, `service_restart`,
  // `docker_recreate`) NÃO TEM PLANO E NÃO PRECISA: não passa por dry-run,
  // porque mutação reversível não se enumera. O resultado era que reiniciar
  // um container pelo chat — o caso de uso mais comum do sistema — mostrava
  // "Este plano não está mais disponível para confirmação" e NENHUM botão.
  //
  // São duas situações OPOSTAS que colapsavam na mesma:
  //   "não tem plano porque não precisa"  (degrau 2 — cartão simples)
  //   "tinha plano e ele sumiu/expirou"   (degrau 3 — recusa honesta)
  //
  // `degrau` ausente cai no lado CONSERVADOR (exige plano) de propósito:
  // proposta gravada antes desta task não carrega o campo, e ela é de
  // degrau 3 — dar a ela um botão habilitado sem plano produziria o
  // botão-que-só-falha (`plano_obrigatorio` no clique). Mesma lógica de
  // `can_confirm` ausente → `false`: deny-by-default, nunca fail-open.
  const exigePlano = plano.degrau == null ? true : plano.degrau === 3
  const bloqueadoPorFaltaDePlano = exigePlano && semPlano
  const itens = plano.itens ?? null
  const temItens = Array.isArray(itens) && itens.length > 0
  const algumCaminhoAnomalo = temItens && itens.some((i) => pathAnomalo(i.path))
  const totalBytes = plano.tipo_plano === 'enumerado'
    ? (plano.estimativa_bytes ?? (temItens ? itens.reduce((acc, i) => acc + i.bytes, 0) : 0))
    : null

  // Requisito 3: plano estimado nunca promete um número — mesmo que
  // `estimativa_bytes` venha preenchido (o backend hoje só manda `null`,
  // mas este card não confia nisso: a regra é do TIPO de plano, não do
  // valor recebido).
  const mostrarTabela = exigePlano && plano.tipo_plano === 'enumerado' && temItens

  // Requisito 3 (produção): hostname digitado precisa bater com o alvo.
  const hostnameConfere = hostnameDigitado.trim() === hostname
  const bloqueadoPorHostname = isProd && !hostnameConfere
  const podeClicarConfirmar =
    canConfirm && !bloqueadoPorFaltaDePlano && !bloqueadoPorHostname

  // Validade do plano (Task 16). Só faz sentido enquanto há um plano vivo a
  // confirmar: depois de executado/recusado o prazo não decide mais nada, e
  // degrau 2 não tem prazo nenhum a mostrar.
  const validade =
    isPending && exigePlano && !semPlano ? formatarValidade(plano.expires_at) : null

  const mostraAntesEDepois = status === 'executed' && !!resultado?.remedicao

  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      style={{
        // Sem barra colorida na lateral (N16, docs/DESIGN_SYSTEM.md): a borda do
        // cartão é a de 1 px do tema.
        borderColor: 'var(--ai-border-light)',
        maxWidth: 520,
        fontFamily: '"Barlow", Arial, sans-serif',
      }}
    >
      {/* Cabeçalho */}
      <Group gap="xs" mb="xs" wrap="nowrap" align="flex-start">
        <IconServerBolt size={16} style={{ marginTop: 2, flexShrink: 0 }} />
        <Stack gap={2} style={{ flex: 1 }}>
          <Text size="sm" fw={600}>
            {verboLabel(plano.verbo)}
          </Text>
          <Text size="xs" c="dimmed" ff="monospace">
            {hostname}
          </Text>
        </Stack>
        {!isPending && (
          <Badge size="xs" color={STATUS_COLORS[status]} variant="light">
            {STATUS_LABELS[status]}
          </Badge>
        )}
      </Group>

      {/* Depois de executado: o antes e o depois da montagem do alerta (MR 3) */}
      {mostraAntesEDepois && resultado?.remedicao && (
        <AntesEDepois
          remedicao={resultado.remedicao}
          bytesLiberados={resultado.bytes_liberados ?? null}
        />
      )}

      {/* Spec 2026-09-30 §6.1: a regra nasce de uma ordem concluída que baixou o disco. */}
      {mostraAntesEDepois && resultado?.remedicao && tenantId != null && plano.plan_id && (
        <RegraDaOrdem
          tenantId={tenantId}
          planId={plano.plan_id}
          status={resultado.status}
          remedicao={resultado.remedicao}
          verbo={plano.verbo}
        />
      )}

      {/* Corpo: enumerado com itens, enumerado vazio ("nada a limpar"), ou estimado */}
      {mostrarTabela && (
        <Stack gap={6} mb="sm">
          {algumCaminhoAnomalo && (
            <Alert
              color="yellow"
              variant="light"
              icon={<IconAlertTriangle size={14} />}
              p={6}
            >
              <Text size="xs">
                Nome de arquivo fora do padrão detectado — exibido escapado
                abaixo. A exclusão usa o inode, não o nome, e não é afetada.
              </Text>
            </Alert>
          )}
          <div style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Caminho</Table.Th>
                  <Table.Th>Tamanho</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {itens!.map((item) => {
                  const anomalo = pathAnomalo(item.path)
                  return (
                    <Table.Tr key={item.inode}>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          {anomalo && (
                            <IconAlertTriangle size={12} color="var(--mantine-color-yellow-7)" />
                          )}
                          <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>
                            {anomalo ? JSON.stringify(item.path) : item.path}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace">
                          {formatarBytes(item.bytes)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </div>
          <Group gap={4} justify="flex-start">
            <Text size="xs" fw={600}>Total:</Text>
            <Text size="xs" fw={600} ff="monospace">
              {totalBytes != null ? formatarBytes(totalBytes) : '—'}
            </Text>
          </Group>
        </Stack>
      )}

      {/* TODA mensagem sobre plano é condicionada a `exigePlano`: para degrau
          2 nenhuma delas é verdadeira, e a última era ativamente falsa. */}
      {exigePlano && plano.tipo_plano === 'enumerado' && !temItens && (
        <Text size="xs" c="dimmed" mb="sm">
          Nada a limpar — a varredura não encontrou itens que casem com o filtro.
        </Text>
      )}

      {/* "O resultado dirá quanto foi liberado" é promessa: com o antes e o depois
          na tela, ela já foi cumprida e a linha só repetiria o que não se sabia. */}
      {exigePlano && plano.tipo_plano === 'estimado' && !mostraAntesEDepois && (
        <Text size="xs" c="dimmed" mb="sm">
          {plano.teto_bytes != null ? (
            <>
              Estimativa indisponível; o teto é{' '}
              <Text span size="xs" ff="monospace">até {formatarBytes(plano.teto_bytes)}</Text>
              . O resultado dirá quanto foi liberado.
            </>
          ) : (
            'Plano estimado — sem estimativa prévia; o resultado dirá quanto foi liberado.'
          )}
        </Text>
      )}

      {exigePlano && plano.comando && (
        <Code block mb="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {plano.comando}
        </Code>
      )}

      {exigePlano && semPlano && plano.tipo_plano !== 'enumerado' && (
        <Text size="xs" c="dimmed" mb="sm">
          Este plano não está mais disponível para confirmação.
        </Text>
      )}

      {/* Validade do plano — uma linha, alinhada à esquerda como todo o card */}
      {validade && (
        <Text size="xs" c="dimmed" mb="sm">
          Plano válido até{' '}
          <Text span size="xs" ff="monospace" fw={600} c="inovareTeal.7">
            {validade}
          </Text>
        </Text>
      )}

      {/* TASK-10810 (I3 da rodada 1; Minor da rodada 2 de revisão): o motivo
          do veto do portão de PRD é mostrado INDEPENDENTE de haver plano —
          "sem plano" e "portão fechado" são dois motivos DIFERENTES de não
          poder confirmar. Antes, esta linha vivia dentro do bloco de botões,
          gateado por `!bloqueadoPorFaltaDePlano`: plano ausente E portão
          fechado ao mesmo tempo caía em silêncio total, exatamente o estado
          que o I3 original queria acabar. */}
      {isPending && !canConfirm && motivoBloqueio && (
        <Text size="xs" c="dimmed" mb="sm">
          {motivoBloqueio}
        </Text>
      )}

      {/* Segunda aprovação (spec 2026-09-27 §12): o rodapé é outro — quem pediu
          espera, o time aprova e executa. O servidor decide quem pode. */}
      {isPending && !bloqueadoPorFaltaDePlano && segunda && (
        <RodapeDaSegundaAprovacao
          planId={plano.plan_id}
          hostname={hostname}
          isProd={isProd}
          vetado={!!motivoBloqueio}
          onConfirm={onConfirm}
          isConfirming={isConfirming}
          onDecline={onDecline}
          isDeclining={isDeclining}
        />
      )}

      {/* Confirmação em produção: digitar o hostname de volta */}
      {isPending && !bloqueadoPorFaltaDePlano && !segunda && isProd && (
        <TextInput
          size="xs"
          mb="sm"
          label={
            <Text size="xs">
              Ambiente de produção — digite <Text span ff="monospace" fw={600}>{hostname}</Text> para confirmar
            </Text>
          }
          placeholder={hostname}
          value={hostnameDigitado}
          onChange={(e) => setHostnameDigitado(e.currentTarget.value)}
          styles={{ input: { fontFamily: '"Barlow", Arial, sans-serif' } }}
        />
      )}

      {/* Botões — só quando pendente e há o que confirmar. Para degrau 3
          isso significa "há plano"; para degrau 2 não há plano a esperar, e
          exigir um escondia o botão de reiniciar container PARA SEMPRE. */}
      {isPending && !bloqueadoPorFaltaDePlano && !segunda && (
        <Group gap="xs" mt="xs" wrap="nowrap">
          {canConfirm && (
            <Button
              size="xs"
              color="inovareTeal"
              variant="filled"
              leftSection={<IconTrash size={14} />}
              disabled={!podeClicarConfirmar}
              loading={isConfirming}
              onClick={() => onConfirm?.()}
            >
              Confirmar
            </Button>
          )}
          <Button
            size="xs"
            color="red"
            variant="light"
            loading={isDeclining}
            onClick={() => onDecline?.()}
          >
            Recusar
          </Button>
        </Group>
      )}
    </Card>
  )
}

// ── Antes e depois (MR 3) ─────────────────────────────────────────────────

function formatarPct(v: number): string {
  return `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
}

function posicaoNoLimiar(depois: number, limiar: number): string {
  if (depois < limiar) return 'abaixo do limiar'
  if (depois === limiar) return 'no limiar'
  return 'ainda acima do limiar'
}

/** Os dois percentuais em JetBrains Mono, o limiar e o espaço liberado em texto
 * secundário. `depois` nulo mostra o motivo, nunca um número. */
function AntesEDepois({
  remedicao,
  bytesLiberados,
}: {
  remedicao: HostOrderRemedicao
  bytesLiberados: number | null
}) {
  const depois = remedicao.depois?.uso_pct
  const antes = remedicao.antes?.uso_pct
  const limiar = remedicao.limiar_pct
  const secundario = { color: 'var(--ai-text-secondary)' }
  if (depois == null) {
    return (
      <Text size="xs" mb="sm" style={secundario}>
        Não consegui medir{' '}
        <Text span size="xs" style={{ fontFamily: FONTE_DADO }}>{remedicao.montagem}</Text>
        {' '}de novo ({remedicao.motivo ?? 'sem motivo'}).
      </Text>
    )
  }
  return (
    <Stack gap={4} mb="sm">
      <Text size="xs" fw={600}>
        Disco{' '}
        <Text span size="xs" fw={600} style={{ fontFamily: FONTE_DADO }}>{remedicao.montagem}</Text>
        {' '}depois da ordem
      </Text>
      <Group gap="lg" align="flex-end" justify="flex-start">
        {antes != null && (
          <Stack gap={0}>
            <Text size="xs" style={secundario}>Antes</Text>
            <Text size="sm" fw={600} style={{ fontFamily: FONTE_DADO }}>{formatarPct(antes)}</Text>
          </Stack>
        )}
        <Stack gap={0}>
          <Text size="xs" style={secundario}>Depois</Text>
          <Text size="sm" fw={600} style={{ fontFamily: FONTE_DADO }}>{formatarPct(depois)}</Text>
        </Stack>
        {bytesLiberados != null && (
          <Stack gap={0}>
            <Text size="xs" style={secundario}>Liberado</Text>
            <Text size="sm" fw={600} style={{ fontFamily: FONTE_DADO }}>{formatarBytes(bytesLiberados)}</Text>
          </Stack>
        )}
      </Group>
      {limiar != null && (
        <Text size="xs" style={secundario}>
          {posicaoNoLimiar(depois, limiar)} do alerta (limiar {formatarPct(limiar)})
        </Text>
      )}
    </Stack>
  )
}

// ── Regra a partir da ordem (spec 2026-09-30 §6.1) ─────────────────────────

/** Quem pediu a ordem decide o servidor (`e_o_solicitante` do estado do plano): o
 * cartão não conhece o id do solicitante. Só consulta quando as condições que a tela
 * enxerga sozinha já valem — ordem concluída, host do alerta e disco que baixou —
 * para o cartão sem regra possível continuar sem React Query. */
function RegraDaOrdem({
  tenantId,
  planId,
  status,
  remedicao,
  verbo,
}: {
  tenantId: number
  planId: string
  status: string | null | undefined
  remedicao: HostOrderRemedicao
  verbo: string
}) {
  const antes = remedicao.antes?.uso_pct
  const depois = remedicao.depois?.uso_pct
  const host = remedicao.alerta_host ?? ''
  const limiar = remedicao.limiar_pct
  // Sem limiar o servidor recusaria (`limiar_desconhecido`): a regra não teria o que copiar.
  if (status !== 'concluida' || !host || antes == null || depois == null || !(depois < antes)
      || limiar == null || !verbo) {
    return null
  }
  return (
    <ConsultaDoSolicitante
      tenantId={tenantId} planId={planId} host={host} antes={antes} depois={depois}
      status={status} montagem={remedicao.montagem} limiarPct={limiar} verbo={verbo}
    />
  )
}

function ConsultaDoSolicitante(props: {
  tenantId: number; planId: string; host: string; antes: number; depois: number; status: string
  montagem: string; limiarPct: number; verbo: string
}) {
  const { data: estado } = useEstadoDoPlano(props.planId, true)
  return <CriarRegraDaOrdem {...props} eOSolicitante={estado?.e_o_solicitante === true} />
}

// ── Segunda aprovação ──────────────────────────────────────────────────────

const MOTIVOS: Record<string, string> = {
  papel_insuficiente: 'Aprovar exige o papel DevOps ou Gestor neste ambiente.',
  permissao_ausente: 'Você não tem as permissões para aprovar esta ordem neste ambiente.',
  aprovado: 'Aprovado por outro integrante do time DevOps; a execução está em andamento.',
  executado: 'Aprovado e executado por outro integrante do time DevOps.',
  recusado: 'Pedido recusado; nada foi executado.',
  substituido: 'Este plano foi substituído por um mais novo; nada foi executado.',
  expirado: 'o plano venceu sem aprovação; nada foi executado',
}

/** Rodapé do cartão quando a ordem exige a aprovação de outro integrante.
 * Componente à parte para só ele depender do React Query: o cartão sem segunda
 * aprovação continua renderizável sem provider. */
function RodapeDaSegundaAprovacao({
  planId,
  hostname,
  isProd,
  vetado,
  onConfirm,
  isConfirming,
  onDecline,
  isDeclining,
}: {
  planId: string | null
  hostname: string
  isProd: boolean
  vetado: boolean
  onConfirm?: () => void
  isConfirming: boolean
  onDecline?: () => void
  isDeclining: boolean
}) {
  const [hostnameDigitado, setHostnameDigitado] = useState('')
  const { data: estado, isLoading, isError } = useEstadoDoPlano(planId, true)

  if (isLoading) {
    return <Text size="xs" c="dimmed">Consultando a aprovação…</Text>
  }
  if (isError || !estado) {
    return <Text size="xs" c="dimmed">Não consegui ler o estado da aprovação deste plano.</Text>
  }

  const ate = formatarValidade(estado.expira_em)
  if (estado.estado === 'aguardando' && estado.e_o_solicitante) {
    return (
      <Stack gap={6}>
        <Text size="xs">
          Pedido enviado; aguardando a aprovação de outro integrante do time DevOps
          {ate ? (
            <>
              {' '}até{' '}
              <Text span size="xs" ff="monospace" fw={600}>{ate}</Text>
            </>
          ) : null}
          .
        </Text>
        <Group gap="xs">
          <Button size="xs" color="red" variant="light" loading={isDeclining}
                  onClick={() => onDecline?.()}>
            Cancelar pedido
          </Button>
        </Group>
      </Stack>
    )
  }

  if (estado.estado === 'aguardando' && estado.pode_aprovar && !vetado) {
    const confere = !isProd || hostnameDigitado.trim() === hostname
    return (
      <Stack gap={6}>
        <Text size="xs">
          Pedido de {estado.solicitante || 'outro integrante'}. Ao aprovar, a execução roda
          com você como executor, numa GMUD aberta automaticamente.
        </Text>
        {isProd && (
          <TextInput
            size="xs"
            label={
              <Text size="xs">
                Ambiente de produção — digite <Text span ff="monospace" fw={600}>{hostname}</Text> para aprovar
              </Text>
            }
            placeholder={hostname}
            value={hostnameDigitado}
            onChange={(e) => setHostnameDigitado(e.currentTarget.value)}
          />
        )}
        <Group gap="xs" wrap="nowrap">
          <Button size="xs" color="inovareTeal" variant="filled"
                  leftSection={<IconTrash size={14} />} disabled={!confere}
                  loading={isConfirming} onClick={() => onConfirm?.()}>
            Aprovar e executar
          </Button>
          <Button size="xs" color="red" variant="light" loading={isDeclining}
                  onClick={() => onDecline?.()}>
            Recusar
          </Button>
        </Group>
      </Stack>
    )
  }

  const chave = estado.estado === 'aguardando' ? (estado.motivo ?? '') : estado.estado
  const texto = MOTIVOS[chave] ?? (estado.motivo || null)
  return texto ? <Text size="xs" c="dimmed">{texto}</Text> : null
}
